import React, { memo, useCallback, useMemo } from 'react';
import { View, FlatList, ScrollView } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  LoadingContainer,
  IconButton,
  Icon,
  Space,
  GradiantBox,
} from '@redlink/components/atoms';
import {
  PreviewUserProfileInfo,
  PreviewListContainer,
  MoreButton,
  PreviewResumeQR,
  PreviewLinkItem,
} from '@redlink/components/molecules';
import {
  useQuery,
  getPagePreviewProfileQuery,
  getPagePreviewSettingQuery,
} from '@redlink/services';
import { useLocale } from '@redlink/assets/locale';
import {
  PreviewGalleryPostCard,
  PreviewShopSimplePostCard,
} from '@redlink/components/muscles';
import { useValues } from '@redlink/assets/values';
import { useNavigation } from '@react-navigation/native';

export const PreviewProfilePage = memo(({ pageName }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { routes, staticData } = useValues();
  const { navigate } = useNavigation();

  const {
    loading,
    data: {
      PageProfilePreview: {
        fullname,
        emailAddress,
        avatar,
        bookmarkedLinks = [],
        bio,
        cellphoneNumber,
        qrCodeUrl,
        links = [],
        posts = [],
        products = [],
      } = {},
    },
  } = useQuery(getPagePreviewProfileQuery, { variables: { pageName } });

  const {
    data: { PageSettingPreview: { showQrCode, visibleTabs = [] } = {} },
  } = useQuery(getPagePreviewSettingQuery, {
    variables: { pageName },
  });

  const tabIsVisible = (tab) => visibleTabs.some((p) => p === tab);

  const twoColumnLinks = useMemo(() => {
    const slicedLinks = links.slice(0, 6);
    const columnCount = Math.ceil(slicedLinks.length / 2.0);
    return Array.from({ length: columnCount || 0 }).map((_, i) => {
      var firstItem = slicedLinks[i * 2];
      var secondItem = slicedLinks[i * 2 + 1];
      return { firstItem, secondItem };
    });
  }, [links]);

  const renderIconLinks = useCallback(
    ({ item: { logoName, shortLinkUrl } }) => {
      return (
        <IconButton
          style={styles.linkItemContainer}
          onPress={() => window.open(shortLinkUrl, '_blank')}
        >
          <Icon color={colors.coverText} name={logoName} />
        </IconButton>
      );
    },
    [colors, styles]
  );

  const renderLinks = useCallback(
    ({ item: { firstItem, secondItem }, index }) => {
      return (
        <View style={styles.twoColumnLinkContainer}>
          <PreviewLinkItem
            numberOfLines={1}
            logoName={firstItem.logoName}
            title={firstItem.title}
            externalUrl={firstItem.shortLinkUrl}
          />
          {secondItem && (
            <>
              <Space size={8} />
              <PreviewLinkItem
                numberOfLines={1}
                logoName={secondItem.logoName}
                title={secondItem.title}
                externalUrl={secondItem.shortLinkUrl}
              />
            </>
          )}
        </View>
      );
    },
    []
  );

  const hasQrCodeUrl = !!qrCodeUrl && showQrCode;
  const hasLinks = !!bookmarkedLinks;

  return (
    <LoadingContainer
      isInitial
      hasOverlay={false}
      isBusy={loading}
      style={styles.container}
    >
      <ScrollView
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        <GradiantBox
          style={styles.coverContainer}
          color1={colors.coverBackaground}
          color2={
            colors.coverHasGradiant
              ? colors.background
              : colors.coverBackaground
          }
        >
          <View style={styles.coverContainer}>
            <PreviewUserProfileInfo
              imageUrl={avatar?.thumbnailUrl}
              title={fullname}
              subtitle={bio}
              emailAddress={emailAddress}
              cellPhone={cellphoneNumber}
              style={styles.bodyContainer}
            />
            {hasLinks && (
              <View style={styles.linksContainer}>
                <FlatList
                  data={bookmarkedLinks}
                  ItemSeparatorComponent={() => <Space size={8} />}
                  renderItem={renderIconLinks}
                  horizontal
                />
              </View>
            )}
            {tabIsVisible(staticData.previewTabsObject.resume) && (
              <>
                <Space size={25} />
                <MoreButton
                  arrowColor={colors.cardBackground}
                  style={styles.resumeButton}
                  title={dictionary.goResume}
                  textStyle={styles.resumeButtonText}
                  onPress={() => navigate(routes.previewResume.key)}
                />
              </>
            )}
          </View>
        </GradiantBox>
        <Space />
        {twoColumnLinks.length > 0 && (
          <PreviewListContainer
            title={dictionary.links}
            onPress={() => navigate(routes.previewLink.key)}
          >
            <FlatList
              horizontal
              data={twoColumnLinks}
              keyExtractor={({ id }) => `${id}`}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[styles.projects, styles.postsContainer]}
              ItemSeparatorComponent={() => <Space size={16} />}
              renderItem={renderLinks}
            />
          </PreviewListContainer>
        )}
        {tabIsVisible(staticData.previewTabsObject.gallery) &&
          posts.length > 0 && (
            <>
              <Space size={25} />
              <PreviewListContainer
                title={dictionary.gallery}
                onPress={() => navigate(routes.previewGallery.key)}
              >
                <FlatList
                  horizontal
                  data={posts}
                  keyExtractor={({ id }) => `${id}`}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={[
                    styles.projects,
                    styles.postsContainer,
                  ]}
                  ItemSeparatorComponent={() => <Space size={8} />}
                  renderItem={({ item }) => (
                    <PreviewGalleryPostCard
                      {...{ item }}
                      style={styles.featuredProjectsStyle}
                      smallTile
                      onPress={() =>
                        navigate(routes.previewPostDetail.key, {
                          pageName,
                          shortLink: item.shortLink,
                        })
                      }
                    />
                  )}
                />
              </PreviewListContainer>
            </>
          )}
        {tabIsVisible(staticData.previewTabsObject.shop) &&
          products.length > 0 && (
            <>
              <Space size={25} />
              <PreviewListContainer
                title={dictionary.shop}
                onPress={() => navigate(routes.previewShop.key)}
              >
                <FlatList
                  horizontal
                  data={products}
                  keyExtractor={({ id }) => `${id}`}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={[
                    styles.projects,
                    styles.postsContainer,
                  ]}
                  ItemSeparatorComponent={() => <Space size={8} />}
                  renderItem={({ item }) => (
                    <PreviewShopSimplePostCard
                      {...{ item }}
                      style={styles.featuredProjectsStyle}
                      smallTile
                      onPress={() => {
                        navigate(routes.previewPostDetail.key, {
                          pageName,
                          shortLink: item.shortLink,
                        });
                      }}
                    />
                  )}
                />
              </PreviewListContainer>
            </>
          )}
        <Space size={48} />
        {hasQrCodeUrl && <PreviewResumeQR qrCode={qrCodeUrl} size={64} />}
        <Space size={16} />
      </ScrollView>
    </LoadingContainer>
  );
});

const useStyle = createUseStyle(({ colors, size, contentWidth }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  bodyContainer: {},
  linksContainer: {
    alignItems: 'center',
    marginTop: 8,
  },
  linkItemContainer: {
    backgroundColor: colors.transparent,
  },
  scrollContainer: {
    paddingBottom: 60,
  },
  coverContainer: {
    padding: 40,
  },
  resumeButton: {
    backgroundColor: colors.primary,
    borderRadius: 50,
    alignSelf: 'center',
    borderWidth: 0,
    padding: 8,
    paddingStart: 16,
  },
  resumeButtonText: {
    color: colors.cardBackground,
  },
  linksScrollContainer: {
    paddingHorizontal: 0,
  },
  postsContainer: {
    paddingHorizontal: 16,
  },
  twoColumnLinkContainer: {
    justifyContent: 'flex-start',
    width: contentWidth - 4 * 16,
  },
}));
